import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { partyText } from "../../static/materialLine";
import { PartyOptionProps } from "../../types/materialLine";
import {
  VmcReceiveDateBy,
  VmcReceiveListProps,
  VmcReceiveProps,
  VmcReceiveSortBy,
  VmcReceiveSortOrder,
  OpenVmcOrderProps,
} from "../../types/vmcReceive";

export interface VmcReceivesState {
  receives: VmcReceiveListProps[];
  totalReceives: number;
  currentReceive: VmcReceiveProps | null;
  currentReceiveLoading: boolean;
  partyOptions: PartyOptionProps[];
  filterPartyOptions: PartyOptionProps[];
  openOrders: OpenVmcOrderProps[];
  openOrdersLoading: boolean;
  openOrdersRequestId: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: VmcReceivesState = {
  receives: [],
  totalReceives: 0,
  currentReceive: null,
  currentReceiveLoading: false,
  partyOptions: [],
  filterPartyOptions: [],
  openOrders: [],
  openOrdersLoading: false,
  openOrdersRequestId: null,
  loading: false,
  error: null,
};

const defaultError = "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";

type FetchVmcReceiveListDataProps = {
  page: number;
  size?: number;
  search?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  party_id?: number | null;
  date_by?: VmcReceiveDateBy;
  sort_by?: VmcReceiveSortBy;
  sort_order?: VmcReceiveSortOrder;
};

export const fetchVmcReceiveListData = createAsyncThunk(
  "fetchVmcReceiveListData",
  async (req: FetchVmcReceiveListDataProps) => {
    let response: any;
    await GET(`/v1/admin/vmc-receives`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchVmcReceiveDetail = createAsyncThunk(
  "fetchVmcReceiveDetail",
  async (id: number) => {
    let response: any;
    await GET(`/v1/admin/vmc-receives/${id}`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// The parties with open orders; partyId keeps an edited receive's own party in the list.
export const fetchVmcReceiveParties = createAsyncThunk(
  "fetchVmcReceiveParties",
  async (partyId?: number | null) => {
    let response: any;
    await GET(`/v1/admin/vmc-receives/parties`, {
      party_id: partyId || null,
    })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// The list filter's parties: every VMC party, active or not, and every party that has receives.
export const fetchVmcReceiveFilterParties = createAsyncThunk(
  "fetchVmcReceiveFilterParties",
  async () => {
    let response: any;
    await GET(`/v1/admin/vmc-receives/parties`, { all: 1 })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// The party's open orders for the receive form; receive_id keeps an edited receive's own orders and leaves out its
// own pieces.
export const fetchVmcReceiveOpenOrders = createAsyncThunk(
  "fetchVmcReceiveOpenOrders",
  async (req: { party_id: number; receive_id?: number | null }) => {
    let response: any;
    await GET(`/v1/admin/vmc-receives/orders`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const vmcReceivesSlice = createSlice({
  name: "vmcReceives",
  initialState,
  reducers: {
    clearCurrentVmcReceive: (state) => {
      state.currentReceive = null;
      state.currentReceiveLoading = false;
    },
    clearVmcReceiveOpenOrders: (state) => {
      state.openOrders = [];
      state.openOrdersLoading = false;
      state.openOrdersRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchVmcReceiveListData.pending, (state) => {
      state.receives = [];
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchVmcReceiveListData.fulfilled,
      (state, action: any) => {
        state.loading = false;
        if (action.payload.data.status === "success") {
          state.receives = action.payload.data.data.records;
          state.totalReceives = action.payload.data.data.total_records;
          state.error = null;
        } else {
          state.receives = [];
          state.error = action?.payload?.data?.message || defaultError;
        }
      }
    );
    builder.addCase(
      fetchVmcReceiveListData.rejected,
      (state, action: any) => {
        state.receives = [];
        state.loading = false;
        state.error = action?.payload?.data?.message || defaultError;
      }
    );

    builder.addCase(fetchVmcReceiveDetail.pending, (state) => {
      state.currentReceiveLoading = true;
      state.currentReceive = null;
    });
    builder.addCase(
      fetchVmcReceiveDetail.fulfilled,
      (state, action: any) => {
        state.currentReceiveLoading = false;
        if (action.payload.data.status === "success") {
          state.currentReceive = action.payload.data.data;
        }
      }
    );
    builder.addCase(fetchVmcReceiveDetail.rejected, (state) => {
      state.currentReceiveLoading = false;
      state.currentReceive = null;
    });

    builder.addCase(
      fetchVmcReceiveParties.fulfilled,
      (state, action: any) => {
        if (action.payload.data.status === "success") {
          state.partyOptions = action.payload.data.data.records.map(
            (party: any) => ({ value: party.id, label: partyText(party) })
          );
        }
      }
    );

    builder.addCase(
      fetchVmcReceiveFilterParties.fulfilled,
      (state, action: any) => {
        if (action.payload.data.status === "success") {
          state.filterPartyOptions = action.payload.data.data.records.map(
            (party: any) => ({ value: party.id, label: partyText(party) })
          );
        }
      }
    );

    // Only the latest request counts, so a slow answer for a party picked earlier can't overwrite it.
    builder.addCase(fetchVmcReceiveOpenOrders.pending, (state, action) => {
      state.openOrders = [];
      state.openOrdersLoading = true;
      state.openOrdersRequestId = action.meta.requestId;
    });
    builder.addCase(
      fetchVmcReceiveOpenOrders.fulfilled,
      (state, action: any) => {
        if (action.meta.requestId !== state.openOrdersRequestId) return;
        state.openOrdersLoading = false;
        state.openOrders =
          action.payload.data.status === "success"
            ? action.payload.data.data.records
            : [];
      }
    );
    builder.addCase(
      fetchVmcReceiveOpenOrders.rejected,
      (state, action) => {
        if (action.meta.requestId !== state.openOrdersRequestId) return;
        state.openOrdersLoading = false;
      }
    );
  },
});

export const { clearCurrentVmcReceive, clearVmcReceiveOpenOrders } =
  vmcReceivesSlice.actions;

export const getVmcReceives = (state: RootState) =>
  state.vmcReceives.receives;
export const getTotalVmcReceives = (state: RootState) =>
  state.vmcReceives.totalReceives;
export const getCurrentVmcReceive = (state: RootState) =>
  state.vmcReceives.currentReceive;
export const getCurrentVmcReceiveLoading = (state: RootState) =>
  state.vmcReceives.currentReceiveLoading;
export const getVmcReceivePartyOptions = (state: RootState) =>
  state.vmcReceives.partyOptions;
export const getVmcReceiveFilterPartyOptions = (state: RootState) =>
  state.vmcReceives.filterPartyOptions;
export const getVmcReceiveOpenOrders = (state: RootState) =>
  state.vmcReceives.openOrders;
export const getVmcReceiveOpenOrdersLoading = (state: RootState) =>
  state.vmcReceives.openOrdersLoading;

export default vmcReceivesSlice.reducer;
